import React, { useState, useMemo } from 'react';
import { DrawerHeader } from '@components/drawer-header';
import {
    Card,
    Typography,
    Stack,
    CircularProgress,
    Box,
    Paper,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    TableSortLabel,
    InputAdornment,
    IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useQuery } from '@tanstack/react-query';
import { getAllTidsStatus } from './api';

export function AdminTidsPage() {
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [filters, setFilters] = useState({
        tid: '',
        operation: '',
        machine_id: '',
        apelido: '',
        sector: '',
        ccid: ''
    });

    const [sortField, setSortField] = useState<'tid' | 'operation' | 'machine_id' | 'apelido' | 'sector' | 'last_heartbeat_time' | 'ccid'>('last_heartbeat_time');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const handleSort = (field: typeof sortField) => {
        const isAsc = sortField === field && sortDir === 'asc';
        setSortDir(isAsc ? 'desc' : 'asc');
        setSortField(field);
    };

    const handleFilterChange = (field: keyof typeof filters, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(0);
    };

    const tidsQuery = useQuery({
        queryKey: ['admin-tids'],
        queryFn: getAllTidsStatus,
    });

    const filteredData = useMemo(() => {
        if (!tidsQuery.data) return [];

        const result = tidsQuery.data.filter((item) => {
            const matchTid = String(item.tid).includes(filters.tid.toLowerCase());
            const matchOp = (item.operation || '').toLowerCase().includes(filters.operation.toLowerCase());
            const matchMachine = (item.machine_id || '').toLowerCase().includes(filters.machine_id.toLowerCase());
            const matchApelido = (item.apelido || '').toLowerCase().includes(filters.apelido.toLowerCase());
            const matchSector = (item.sector || '').toLowerCase().includes(filters.sector.toLowerCase());
            const matchCcid = (item.ccid || '').toLowerCase().includes(filters.ccid.toLowerCase());
            return matchTid && matchOp && matchMachine && matchApelido && matchSector && matchCcid;
        });

        result.sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (sortField === 'last_heartbeat_time') {
                const numA = Number(valA) || 0;
                const numB = Number(valB) || 0;

                if (numA < numB) return sortDir === 'asc' ? -1 : 1;
                if (numA > numB) return sortDir === 'asc' ? 1 : -1;
                return 0;
            }

            if (valA === null || valA === undefined) valA = '';
            if (valB === null || valB === undefined) valB = '';

            if (typeof valA === 'string' && typeof valB === 'string') {
                valA = valA.toLowerCase();
                valB = valB.toLowerCase();
            }

            if (valA < valB) {
                return sortDir === 'asc' ? -1 : 1;
            }
            if (valA > valB) {
                return sortDir === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return result;
    }, [tidsQuery.data, filters, sortField, sortDir]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const { min5, min15, min30 } = useMemo(() => {
        let min5 = 0;
        let min15 = 0;
        let min30 = 0;
        const nowSecs = Date.now() / 1000;

        filteredData.forEach((item: any) => {
            const diff = nowSecs - item.last_heartbeat_time;
            if (diff <= 300) {
                min5++;
            }
            if (diff <= 900) {
                min15++;
            }
            if (diff <= 1800) {
                min30++;
            }
        });

        return { min5, min15, min30 };
    }, [filteredData]);

    return (
        <DrawerHeader title="Administração de TIDs">
            <Box sx={{ mb: 2 }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 2.5, md: 3 },
                        bgcolor: 'background.paper',
                        borderLeft: `6px solid ${theme.palette.primary.main}`,
                        borderRadius: 2,
                        mb: 1
                    }}
                >
                    <Box>
                        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
                            ADMINISTRAÇÃO GLOBAL
                        </Typography>
                        <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Status dos TIDs</Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                            Acompanhe todos os TIDs registrados globalmente, suas respectivas operações, setores e ID de máquina.
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Paper sx={{ p: 2, flex: 1, minWidth: '150px', borderLeft: '6px solid #4caf50' }}>
                    <Typography variant="body2" color="text.secondary">Online (&lt; 5 min)</Typography>
                    <Typography variant="h5" fontWeight="bold">{min5}</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, minWidth: '150px', borderLeft: '6px solid #ff9800' }}>
                    <Typography variant="body2" color="text.secondary">Online (&lt; 15 min)</Typography>
                    <Typography variant="h5" fontWeight="bold">{min15}</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, minWidth: '150px', borderLeft: '6px solid #f44336' }}>
                    <Typography variant="body2" color="text.secondary">Online (&gt; 30 min)</Typography>
                    <Typography variant="h5" fontWeight="bold">{min30}</Typography>
                </Paper>
            </Box>

            <Card sx={{ mt: 3 }}>


                {tidsQuery.isLoading ? (
                    <Stack alignItems="center" my={4}>
                        <CircularProgress />
                        <Typography variant="body2" mt={2}>Carregando dados...</Typography>
                    </Stack>
                ) : tidsQuery.isError ? (
                    <Stack alignItems="center" my={4}>
                        <Typography color="error">Erro ao carregar dados. Verifique suas permissões de administrador.</Typography>
                    </Stack>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'tid'}
                                            direction={sortField === 'tid' ? sortDir : 'asc'}
                                            onClick={() => handleSort('tid')}
                                        >
                                            <strong>TID</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'operation'}
                                            direction={sortField === 'operation' ? sortDir : 'asc'}
                                            onClick={() => handleSort('operation')}
                                        >
                                            <strong>Operação</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'machine_id'}
                                            direction={sortField === 'machine_id' ? sortDir : 'asc'}
                                            onClick={() => handleSort('machine_id')}
                                        >
                                            <strong>ID da Máquina</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'apelido'}
                                            direction={sortField === 'apelido' ? sortDir : 'asc'}
                                            onClick={() => handleSort('apelido')}
                                        >
                                            <strong>Apelido</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'sector'}
                                            direction={sortField === 'sector' ? sortDir : 'asc'}
                                            onClick={() => handleSort('sector')}
                                        >
                                            <strong>Setor</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'ccid'}
                                            direction={sortField === 'ccid' ? sortDir : 'asc'}
                                            onClick={() => handleSort('ccid')}
                                        >
                                            <strong>CCID</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'last_heartbeat_time'}
                                            direction={sortField === 'last_heartbeat_time' ? sortDir : 'asc'}
                                            onClick={() => handleSort('last_heartbeat_time')}
                                        >
                                            <strong>Último Heartbeat</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ py: 1 }}>
                                        <TextField
                                            placeholder="Filtrar TID"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={filters.tid}
                                            onChange={(e) => handleFilterChange('tid', e.target.value)}
                                            InputProps={{
                                                endAdornment: filters.tid ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => handleFilterChange('tid', '')}><ClearIcon fontSize="small" /></IconButton>
                                                    </InputAdornment>
                                                ) : null
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <TextField
                                            placeholder="Filtrar Operação"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={filters.operation}
                                            onChange={(e) => handleFilterChange('operation', e.target.value)}
                                            InputProps={{
                                                endAdornment: filters.operation ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => handleFilterChange('operation', '')}><ClearIcon fontSize="small" /></IconButton>
                                                    </InputAdornment>
                                                ) : null
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <TextField
                                            placeholder="Filtrar Máquina"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={filters.machine_id}
                                            onChange={(e) => handleFilterChange('machine_id', e.target.value)}
                                            InputProps={{
                                                endAdornment: filters.machine_id ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => handleFilterChange('machine_id', '')}><ClearIcon fontSize="small" /></IconButton>
                                                    </InputAdornment>
                                                ) : null
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <TextField
                                            placeholder="Filtrar Apelido"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={filters.apelido}
                                            onChange={(e) => handleFilterChange('apelido', e.target.value)}
                                            InputProps={{
                                                endAdornment: filters.apelido ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => handleFilterChange('apelido', '')}><ClearIcon fontSize="small" /></IconButton>
                                                    </InputAdornment>
                                                ) : null
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <TextField
                                            placeholder="Filtrar Setor"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={filters.sector}
                                            onChange={(e) => handleFilterChange('sector', e.target.value)}
                                            InputProps={{
                                                endAdornment: filters.sector ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => handleFilterChange('sector', '')}><ClearIcon fontSize="small" /></IconButton>
                                                    </InputAdornment>
                                                ) : null
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <TextField
                                            placeholder="Filtrar CCID"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={filters.ccid}
                                            onChange={(e) => handleFilterChange('ccid', e.target.value)}
                                            InputProps={{
                                                endAdornment: filters.ccid ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => handleFilterChange('ccid', '')}><ClearIcon fontSize="small" /></IconButton>
                                                    </InputAdornment>
                                                ) : null
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row) => (
                                        <TableRow key={row.tid}>
                                            <TableCell>{row.tid}</TableCell>
                                            <TableCell>{row.operation || '-'}</TableCell>
                                            <TableCell>{row.machine_id || '-'}</TableCell>
                                            <TableCell>{row.apelido || '-'}</TableCell>
                                            <TableCell>{row.sector || '-'}</TableCell>
                                            <TableCell>{row.ccid || '-'}</TableCell>
                                            <TableCell>
                                                {row.last_heartbeat_time
                                                    ? new Date(row.last_heartbeat_time * 1000).toLocaleString()
                                                    : 'Sem Registro'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                {filteredData.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">Nenhum TID encontrado</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={filteredData.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Linhas por página"
                        />
                    </TableContainer>
                )}
            </Card>
        </DrawerHeader>
    );
}
